import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getProducts } from '../../services/api';
import type { Product } from '../../types';

const ProductsManagement: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    getProducts(false, true)
      .then(setProducts)
      .catch(e => setError(e.message ?? 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <h1 className="text-2xl font-semibold">Products</h1>
        <Link
          to="/admin/products/new"
          className="bg-primary text-white rounded px-4 py-2 text-sm font-medium"
        >
          New product
        </Link>
      </div>

      {loading && <div className="text-text-light">Loading…</div>}
      {error && <div className="text-red-600 text-sm">Error: {error}</div>}

      {!loading && !error && products.length === 0 && (
        <div className="text-text-light text-sm">
          No products yet. Click "New product" to create one.
        </div>
      )}

      {products.length > 0 && (
        <table className="hidden sm:table w-full bg-white rounded border border-background-dark text-sm">
          <thead className="bg-background-dark/50">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Visibility</th>
              <th className="text-left px-3 py-2">Variants</th>
            </tr>
          </thead>
          <tbody>
            {products.map(p => (
              <tr
                key={p.product_id}
                onClick={() => navigate(`/admin/products/${p.product_id}`)}
                className="border-t border-background-dark cursor-pointer hover:bg-background-dark/30"
              >
                <td className="px-3 py-2">{p.name}</td>
                <td className="px-3 py-2 capitalize">{p.status}</td>
                <td className="px-3 py-2">
                  {p.library_slug ? (
                    <span className="text-xs font-mono bg-primary/10 text-primary rounded px-2 py-0.5">
                      {p.library_slug}
                    </span>
                  ) : (
                    <span className="text-xs text-text-light">Public</span>
                  )}
                </td>
                <td className="px-3 py-2">{p.variants?.length ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Phone layout: the same four fields, stacked. */}
      {products.length > 0 && (
        <ul className="sm:hidden space-y-2">
          {products.map(p => (
            <li key={p.product_id}>
              <button
                type="button"
                onClick={() => navigate(`/admin/products/${p.product_id}`)}
                className="w-full text-left bg-white border border-background-dark rounded p-3 active:bg-background-dark/30"
              >
                <div className="font-medium">{p.name}</div>
                <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-text-light">
                  <span className="capitalize">{p.status}</span>
                  <span>·</span>
                  {p.library_slug ? (
                    <span className="font-mono bg-primary/10 text-primary rounded px-2 py-0.5">
                      {p.library_slug}
                    </span>
                  ) : (
                    <span>Public</span>
                  )}
                  <span>·</span>
                  <span>{p.variants?.length ?? 0} variants</span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ProductsManagement;
